import type { FC } from 'react';

import type { PhoneScreen } from '@/types/home.types';

interface PhoneMockupProps {
  screens: PhoneScreen[];
  activeScreenId: PhoneScreen['id'];
  onSelectScreen: (id: PhoneScreen['id']) => void;
}

const PhoneMockup: FC<PhoneMockupProps> = ({ screens, activeScreenId, onSelectScreen }) => (
  <div className="flex flex-col items-center">
    {/* Capped at 100% - 48px so the offset accent block behind the phone can
        hang 24px past both edges without ever creating a sideways scroll. */}
    <div className="relative w-[min(320px,100%_-_48px)]">
      <div aria-hidden className="absolute top-6 -right-6 -bottom-6 left-6 bg-site-accent" />

      <div className="relative rounded-[48px] bg-[#16161a] p-3 shadow-[0_12px_32px_rgba(26,26,31,0.22)] ring-2 ring-[#3a3a42] ring-inset">
        <span aria-hidden className="absolute top-[110px] -left-[3px] h-[30px] w-[3px] rounded-l-sm bg-[#2a2a30]" />
        <span aria-hidden className="absolute top-[156px] -left-[3px] h-[52px] w-[3px] rounded-l-sm bg-[#2a2a30]" />
        <span aria-hidden className="absolute top-[140px] -right-[3px] h-[76px] w-[3px] rounded-r-sm bg-[#2a2a30]" />

        <div className="relative overflow-hidden rounded-[36px] bg-white">
          <div className="flex h-10 items-center justify-between pr-[26px] pl-[30px] font-site text-[14px] leading-normal font-semibold text-[#111]">
            <span>9:41</span>
            <span className="flex items-center gap-[5px]">
              <span aria-hidden className="flex h-[11px] items-end gap-[2px]">
                <span className="h-1 w-[3px] rounded-[1px] bg-[#111]" />
                <span className="h-1.5 w-[3px] rounded-[1px] bg-[#111]" />
                <span className="h-2 w-[3px] rounded-[1px] bg-[#111]" />
                <span className="h-[11px] w-[3px] rounded-[1px] bg-[#111]" />
              </span>
              <span
                aria-hidden
                className="flex h-[11px] w-[22px] rounded-[3px] border-[1.5px] border-[#111] p-px"
              >
                <span className="flex-1 rounded-[1px] bg-[#111]" />
              </span>
            </span>
          </div>

          <span
            aria-hidden
            className="absolute top-2.5 left-1/2 h-[26px] w-[92px] -translate-x-1/2 rounded-[14px] bg-black"
          />

          {screens.map((screen) => (
            <img
              key={screen.id}
              src={screen.src}
              alt={screen.alt}
              width={446}
              height={876}
              loading="lazy"
              className={`block h-auto w-full bg-white ${screen.id === activeScreenId ? '' : 'hidden'}`}
            />
          ))}

          <div className="flex justify-center bg-white pt-2 pb-2.5">
            <span aria-hidden className="h-1 w-[110px] rounded-sm bg-[#111]" />
          </div>
        </div>
      </div>
    </div>

    <div
      role="tablist"
      aria-label="App screens"
      className="mt-11 flex w-fit max-w-full border-2 border-site-ink"
    >
      {screens.map((screen) => {
        const isActive = screen.id === activeScreenId;

        return (
          <button
            key={screen.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelectScreen(screen.id)}
            className={`cursor-pointer px-[18px] py-2.5 font-site text-[14px] leading-normal font-bold transition-colors ${
              isActive ? 'bg-site-ink text-white' : 'bg-transparent text-site-ink hover:bg-site-ink/10'
            }`}
          >
            {screen.label}
          </button>
        );
      })}
    </div>
  </div>
);

export default PhoneMockup;
